/**
 * Payroll details handoff (flag hr_sensitive_handoff, OFF until legal review). A new hire sends their
 * SSN and direct-deposit details to the hiring organization's owner through Arevina instead of reading
 * them out over the phone. Pure rules shared by the pages and the server (copied to
 * functions/src/generated by scripts/syncFunctionsGenerated.mjs): the flag, format checks (never a
 * verification service), masking, and the deletion windows. The security design is in
 * docs/sensitive-handoff-security.md; the server is functions/src/payrollHandoff.ts.
 *
 * Keep this file free of imports.
 */

export const HR_SENSITIVE_HANDOFF_FLAG = 'hr_sensitive_handoff';
export const HR_SENSITIVE_HANDOFF_FLAG_LABEL = 'Payroll details handoff (SSN and direct deposit)';
export const HR_SENSITIVE_HANDOFF_FLAG_DESCRIPTION =
  "The organization's owner can ask a hired person for their SSN and direct-deposit details; the person submits them through Arevina, encrypted with Cloud KMS, and the owner reveals them once with a recent sign-in. Deleted automatically 7 days after the first view, or 14 days after submission if never viewed. Keep OFF until the legal checklist in docs/sensitive-handoff-security.md is done and a production key exists.";

/** Data is deleted this long after the owner first reveals it. */
export const PAYROLL_DELETE_DAYS_AFTER_VIEW = 7;
/** ...or this long after it was submitted, if nobody ever reveals it. */
export const PAYROLL_DELETE_DAYS_IF_UNVIEWED = 14;
/** An unanswered request expires after this many days. */
export const PAYROLL_REQUEST_EXPIRES_DAYS = 14;
/** A reveal needs a sign-in no older than this (seconds). */
export const PAYROLL_REVEAL_MAX_AUTH_AGE_SECONDS = 5 * 60;

export const PAYROLL_ACCOUNT_TYPES = ['checking', 'savings'] as const;
export type PayrollAccountType = (typeof PAYROLL_ACCOUNT_TYPES)[number];

export type PayrollRequestStatus = 'requested' | 'submitted' | 'viewed' | 'withdrawn' | 'deleted' | 'expired' | 'cancelled';

/** What is kept outside the encrypted record: no part of the SSN, only the last 4 of the bank numbers. */
export type PayrollMasked = { routingLast4: string; accountLast4: string; accountType: PayrollAccountType };

/** What the person reads on the form (who sees it, how long it is kept). */
export function payrollNotice(orgName: string): string[] {
  return [
    `Only the owner of ${orgName || 'this organization'} can see these details, and only after confirming it is them by signing in again. Every time they view them is recorded.`,
    'Arevina encrypts them as soon as you submit and never shows them anywhere else: not in emails, notifications, exports, search or messages.',
    `They are deleted automatically ${PAYROLL_DELETE_DAYS_AFTER_VIEW} days after the owner first views them, or ${PAYROLL_DELETE_DAYS_IF_UNVIEWED} days after you submit them if nobody views them. You will get a notice when they are deleted.`,
    'You can withdraw them any time until the owner views them.',
    'We only check the format (for example that a routing number is valid); nothing is sent to any verification service.',
  ];
}

const digitsOf = (s: unknown) => (typeof s === 'string' ? s.replace(/[\s-]/g, '') : '');

/**
 * SSN format only: 9 digits (dashes and spaces allowed); area not 000, 666 or 900-999; group not 00;
 * serial not 0000. Nothing is looked up anywhere.
 */
export function validateSsn(raw: unknown): { ok: true; digits: string } | { ok: false; message: string } {
  const d = digitsOf(raw);
  if (!/^\d{9}$/.test(d)) return { ok: false, message: 'Enter the 9 digits of your Social Security number.' };
  const area = d.slice(0, 3), group = d.slice(3, 5), serial = d.slice(5);
  if (area === '000' || area === '666' || area[0] === '9' || group === '00' || serial === '0000') {
    return { ok: false, message: 'That is not a valid Social Security number format.' };
  }
  return { ok: true, digits: d };
}

/** ABA routing number: 9 digits whose weighted sum (3, 7, 1 repeating) is a multiple of 10. */
export function validateRoutingNumber(raw: unknown): { ok: true; digits: string } | { ok: false; message: string } {
  const d = digitsOf(raw);
  if (!/^\d{9}$/.test(d)) return { ok: false, message: 'A routing number has 9 digits.' };
  const n = d.split('').map(Number);
  const sum = 3 * (n[0] + n[3] + n[6]) + 7 * (n[1] + n[4] + n[7]) + (n[2] + n[5] + n[8]);
  if (sum % 10 !== 0 || d === '000000000') return { ok: false, message: 'That routing number is not valid. Check it on a check or with your bank.' };
  return { ok: true, digits: d };
}

/** Account number: 4 to 17 digits. */
export function validateAccountNumber(raw: unknown): { ok: true; digits: string } | { ok: false; message: string } {
  const d = digitsOf(raw);
  if (!/^\d{4,17}$/.test(d)) return { ok: false, message: 'An account number has 4 to 17 digits.' };
  return { ok: true, digits: d };
}

export function validateAccountType(raw: unknown): raw is PayrollAccountType {
  return typeof raw === 'string' && (PAYROLL_ACCOUNT_TYPES as readonly string[]).includes(raw);
}

/** All four fields at once: the first problem per field, or the clean digits. */
export function validatePayrollSubmission(input: { ssn?: unknown; routing?: unknown; account?: unknown; accountConfirm?: unknown; accountType?: unknown }):
  | { ok: true; value: { ssn: string; routing: string; account: string; accountType: PayrollAccountType } }
  | { ok: false; errors: Partial<Record<'ssn' | 'routing' | 'account' | 'accountConfirm' | 'accountType', string>> } {
  const errors: Partial<Record<'ssn' | 'routing' | 'account' | 'accountConfirm' | 'accountType', string>> = {};
  const ssn = validateSsn(input.ssn);
  const routing = validateRoutingNumber(input.routing);
  const account = validateAccountNumber(input.account);
  if (!ssn.ok) errors.ssn = ssn.message;
  if (!routing.ok) errors.routing = routing.message;
  if (!account.ok) errors.account = account.message;
  if (input.accountConfirm !== undefined && account.ok && digitsOf(input.accountConfirm) !== account.digits) errors.accountConfirm = 'The two account numbers do not match.';
  if (!validateAccountType(input.accountType)) errors.accountType = 'Choose checking or savings.';
  if (Object.keys(errors).length || !ssn.ok || !routing.ok || !account.ok) return { ok: false, errors };
  return { ok: true, value: { ssn: ssn.digits, routing: routing.digits, account: account.digits, accountType: input.accountType as PayrollAccountType } };
}

export const last4 = (digits: string) => digits.slice(-4);
/** Masks; the full values are shown only on a reveal. No part of the SSN is kept unencrypted. */
export const MASKED_SSN = '•••-••-••••';
export function maskTail(last: string): string { return `••••${last}`; }

export function payrollMasked(v: { ssn: string; routing: string; account: string; accountType: PayrollAccountType }): PayrollMasked {
  return { routingLast4: last4(v.routing), accountLast4: last4(v.account), accountType: v.accountType };
}

const DAY = 86_400_000;
/** When the submitted details are deleted: 7 days after the first view, else 14 days after submission. */
export function payrollDeleteAfterMs(submittedMs: number, firstViewedMs?: number | null): number {
  return typeof firstViewedMs === 'number' ? firstViewedMs + PAYROLL_DELETE_DAYS_AFTER_VIEW * DAY : submittedMs + PAYROLL_DELETE_DAYS_IF_UNVIEWED * DAY;
}
export function payrollRequestExpiresMs(createdMs: number): number {
  return createdMs + PAYROLL_REQUEST_EXPIRES_DAYS * DAY;
}

export const PAYROLL_STATUS_LABELS: Record<PayrollRequestStatus, string> = {
  requested: 'Requested',
  submitted: 'Submitted, not viewed yet',
  viewed: 'Viewed by the owner',
  withdrawn: 'Withdrawn by the employee',
  deleted: 'Deleted',
  expired: 'Expired (never answered)',
  cancelled: 'Cancelled',
};
